import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * Optional additional-diamonds dropdown: line-item property + priced addon product.
 *
 * Each ATC adds a separate addon line (never qty-merged) using unique `_` properties
 * for parent↔addon linking. Underscore props stay hidden in cart drawer + checkout.
 *
 * Note: product forms include <input name="id">, so never read form.id — use getAttribute('id').
 *
 * @extends {Component}
 */
class AdditionalDiamondsComponent extends Component {
  /** @type {((event: Event) => void) | null} */
  #boundWindowSubmit = null;
  /** @type {((event: Event) => void) | null} */
  #boundDocClick = null;
  /** @type {((event: KeyboardEvent) => void) | null} */
  #boundDocKeydown = null;
  /** @type {((event: Event) => void) | null} */
  #boundVariantUpdate = null;

  connectedCallback() {
    super.connectedCallback();

    const select = this.#getSelect();
    select?.addEventListener('change', this.#onChange);

    this.#boundWindowSubmit = this.#onWindowSubmit.bind(this);
    window.addEventListener('submit', this.#boundWindowSubmit, true);

    // Delegate clicks so morph/re-render does not leave stale per-option listeners.
    this.addEventListener('click', this.#onComponentClick);

    this.#boundDocClick = this.#onDocumentClick.bind(this);
    this.#boundDocKeydown = this.#onDocumentKeydown.bind(this);
    document.addEventListener('click', this.#boundDocClick);
    document.addEventListener('keydown', this.#boundDocKeydown);

    // Re-apply diamond surcharge after variant price morph.
    const section = this.closest('.shopify-section');
    this.#boundVariantUpdate = () => {
      requestAnimationFrame(() => this.#updateDisplayedPrices());
    };
    section?.addEventListener('variant:update', this.#boundVariantUpdate);

    this.#syncHiddenInputs();
    this.#updateDisplayedPrices();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const select = this.#getSelect();
    select?.removeEventListener('change', this.#onChange);

    if (this.#boundWindowSubmit) {
      window.removeEventListener('submit', this.#boundWindowSubmit, true);
    }

    this.removeEventListener('click', this.#onComponentClick);

    if (this.#boundDocClick) document.removeEventListener('click', this.#boundDocClick);
    if (this.#boundDocKeydown) document.removeEventListener('keydown', this.#boundDocKeydown);

    const section = this.closest('.shopify-section');
    if (this.#boundVariantUpdate) {
      section?.removeEventListener('variant:update', this.#boundVariantUpdate);
    }
  }

  #getForm() {
    const formId = this.dataset.formId;
    return formId ? document.getElementById(formId) : null;
  }

  /**
   * Product forms have <input name="id"> which shadows HTMLFormElement.id.
   * @param {HTMLFormElement} form
   */
  #formId(form) {
    return form.getAttribute('id') || '';
  }

  /**
   * Prefer live DOM nodes over possibly detached Component refs after morph.
   * @param {string} refName
   * @param {string} selector
   */
  #liveRef(refName, selector) {
    const fromRef = this.refs?.[refName];
    if (fromRef instanceof HTMLElement && fromRef.isConnected) return fromRef;
    return this.querySelector(selector);
  }

  #getSelect() {
    return /** @type {HTMLSelectElement | null} */ (
      this.#liveRef('select', '[data-additional-diamonds-select]')
    );
  }

  #getTrigger() {
    return /** @type {HTMLButtonElement | null} */ (
      this.#liveRef('trigger', '[data-additional-diamonds-trigger]')
    );
  }

  #getList() {
    return /** @type {HTMLElement | null} */ (
      this.#liveRef('list', '[data-additional-diamonds-list]')
    );
  }

  #getTriggerText() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-additional-diamonds-trigger-text]'));
  }

  #isOpen() {
    const list = this.#getList();
    return Boolean(list && !list.hidden);
  }

  #openList() {
    const list = this.#getList();
    const trigger = this.#getTrigger();
    if (!list || !trigger) return;

    list.hidden = false;
    trigger.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');

    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    list.classList.toggle('is-top', spaceBelow < 260 && rect.top > spaceBelow);
  }

  #closeList() {
    const list = this.#getList();
    const trigger = this.#getTrigger();
    if (!list || !trigger) return;

    list.hidden = true;
    list.classList.remove('is-top');
    trigger.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
  }

  /**
   * @param {Event} event
   */
  #onComponentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-additional-diamonds-trigger]');
    if (trigger && this.contains(trigger)) {
      event.preventDefault();
      event.stopPropagation();
      if (this.#isOpen()) this.#closeList();
      else this.#openList();
      return;
    }

    const option = target.closest('[data-additional-diamonds-option]');
    if (option instanceof HTMLElement && this.contains(option)) {
      event.preventDefault();
      event.stopPropagation();
      this.#selectOption(option);
    }
  };

  /**
   * @param {HTMLElement} option
   */
  #selectOption(option) {
    const value = option.dataset.value || '';
    const variantId = option.dataset.variantId || '';
    const select = this.#getSelect();
    if (!select || !value) return;

    select.value = value;
    const matching = [...select.options].find((opt) => opt.value === value);
    if (matching && variantId) matching.dataset.variantId = variantId;

    this.querySelectorAll('[data-additional-diamonds-option]').forEach((el) => {
      el.setAttribute('aria-selected', el === option ? 'true' : 'false');
    });

    const trigger = this.#getTrigger();
    const triggerText = this.#getTriggerText();
    if (triggerText) triggerText.textContent = value;
    trigger?.classList.add('is-selected');

    this.#closeList();
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * @param {Event} event
   */
  #onDocumentClick(event) {
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;
    this.#closeList();
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onDocumentKeydown(event) {
    if (event.key === 'Escape') this.#closeList();
  }

  #onChange = () => {
    this.#syncHiddenInputs();
    this.#updateDisplayedPrices();
  };

  #syncHiddenInputs() {
    const select = this.#getSelect();
    const propertyInput = /** @type {HTMLInputElement | null} */ (
      this.refs.propertyInput || this.querySelector('[data-additional-diamonds-property]')
    );
    const itemKeyInput = /** @type {HTMLInputElement | null} */ (
      this.refs.itemKeyInput || this.querySelector('[data-additional-diamonds-item-key]')
    );
    const addonVariantInput = /** @type {HTMLInputElement | null} */ (
      this.refs.addonVariantInput || this.querySelector('[data-additional-diamonds-addon-variant]')
    );

    if (!select || !propertyInput || !itemKeyInput || !addonVariantInput) return;

    const option = select.selectedOptions[0];
    const label = option?.value?.trim() || '';
    const variantId = option?.dataset.variantId || '';
    const addonPrice = Number(option?.dataset.price) || 0;

    if (!label || !variantId) {
      propertyInput.value = '';
      propertyInput.disabled = true;
      itemKeyInput.value = '';
      itemKeyInput.disabled = true;
      addonVariantInput.value = '';
      addonVariantInput.disabled = true;
      delete this.dataset.itemKey;
      delete this.dataset.addonVariantId;
      delete this.dataset.selectedLabel;
      delete this.dataset.addonPrice;
      return;
    }

    const itemKey = this.#createItemKey();
    propertyInput.value = label;
    propertyInput.disabled = false;
    itemKeyInput.value = itemKey;
    itemKeyInput.disabled = false;
    addonVariantInput.value = `${variantId}:1:0`;
    addonVariantInput.disabled = false;
    this.dataset.itemKey = itemKey;
    this.dataset.addonVariantId = variantId;
    this.dataset.selectedLabel = label;
    this.dataset.addonPrice = String(addonPrice);
  }

  /**
   * Product price block for this PDP (same section / product).
   * @returns {HTMLElement | null}
   */
  #getProductPriceEl() {
    const section = this.closest('.shopify-section');
    const productId = this.dataset.productId;
    if (!section) return document.querySelector('product-price');

    const scoped = [...section.querySelectorAll('product-price')].find(
      (el) => !productId || el instanceof HTMLElement && el.dataset.productId === productId
    );
    return scoped instanceof HTMLElement ? scoped : null;
  }

  #getBaseVariantPriceCents() {
    const productPrice = this.#getProductPriceEl();
    const container = productPrice?.querySelector('[ref="priceContainer"]');
    const raw =
      (container instanceof HTMLElement && container.dataset.variantPrice) ||
      (productPrice instanceof HTMLElement && productPrice.dataset.variantPrice) ||
      '';
    const cents = Number(raw);
    return Number.isFinite(cents) ? cents : null;
  }

  #getAddonPriceCents() {
    const select = this.#getSelect();
    const option = select?.selectedOptions[0];
    const fromOption = Number(option?.dataset.price);
    if (option?.value && Number.isFinite(fromOption)) return fromOption;
    const fromDataset = Number(this.dataset.addonPrice);
    return this.dataset.selectedLabel && Number.isFinite(fromDataset) ? fromDataset : 0;
  }

  /**
   * Format cents using the shop money format (Liquid `| money` parity).
   * @param {number} cents
   */
  #formatMoney(cents) {
    const productPrice = this.#getProductPriceEl();
    const format =
      this.dataset.moneyFormat ||
      (productPrice instanceof HTMLElement ? productPrice.dataset.moneyFormat : '') ||
      '€{{amount_with_comma_separator}}';

    const value = Number(cents) || 0;
    /**
     * @param {number} amountCents
     * @param {number} precision
     * @param {string} thousands
     * @param {string} decimal
     */
    const withDelimiters = (amountCents, precision, thousands, decimal) => {
      const number = (amountCents / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${dollars}${decimal}${parts[1]}` : dollars;
    };

    /** @type {Record<string, string>} */
    const map = {
      amount: withDelimiters(value, 2, ',', '.'),
      amount_no_decimals: withDelimiters(value, 0, ',', '.'),
      amount_with_comma_separator: withDelimiters(value, 2, '.', ','),
      amount_no_decimals_with_comma_separator: withDelimiters(value, 0, '.', ','),
      amount_with_apostrophe_separator: withDelimiters(value, 2, "'", '.'),
      amount_with_space_separator: withDelimiters(value, 2, ' ', ','),
      amount_no_decimals_with_space_separator: withDelimiters(value, 0, ' ', ','),
    };

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => map[key] || map.amount);
  }

  #getInstallmentPayments() {
    const productPrice = this.#getProductPriceEl();
    const fromPrice =
      productPrice instanceof HTMLElement ? Number(productPrice.dataset.installmentPayments) : NaN;
    if (Number.isFinite(fromPrice) && fromPrice >= 2) return Math.floor(fromPrice);

    const price3x = productPrice?.querySelector('.price-3x');
    const fromSpan =
      price3x instanceof HTMLElement ? Number(price3x.dataset.installmentPayments) : NaN;
    if (Number.isFinite(fromSpan) && fromSpan >= 2) return Math.floor(fromSpan);

    return 3;
  }

  /**
   * Update PDP `.price`, `.price-3x`, and ATC button price to variant base + diamond addon.
   * Display only — does not change ATC / variant picker behaviour.
   */
  #updateDisplayedPrices() {
    const productPrice = this.#getProductPriceEl();
    if (!productPrice) return;

    const baseCents = this.#getBaseVariantPriceCents();
    if (baseCents == null) return;

    const totalCents = baseCents + this.#getAddonPriceCents();
    const formatted = this.#formatMoney(totalCents);
    const payments = this.#getInstallmentPayments();
    const formattedInstallment = this.#formatMoney(Math.floor(totalCents / payments));

    const priceEl = productPrice.querySelector('.price');
    if (priceEl) priceEl.textContent = formatted;

    const price3x = productPrice.querySelector('.price-3x');
    if (price3x) {
      price3x.textContent = `ou ${payments}x ${formattedInstallment}`;
      if (price3x instanceof HTMLElement) {
        price3x.dataset.baseInstallment = String(Math.floor(totalCents / payments));
      }
    }

    // Keep "Ajouter au panier - €…" in sync with the displayed total.
    const form = this.#getForm();
    const section = this.closest('.shopify-section');
    const atcPrice =
      form?.querySelector('.add-to-cart-text__price') ||
      section?.querySelector('.add-to-cart-text__price');
    if (atcPrice) atcPrice.textContent = formatted;
  }

  #createItemKey() {
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  }

  /**
   * @param {HTMLFormElement} form
   */
  #getParentTitle(form) {
    const productTitle = this.dataset.productTitle || '';
    const root =
      form.closest('.product-information, product-info, [data-product-id], .product-details') ||
      form.parentElement;
    const selected = root
      ? [...root.querySelectorAll('input[type="radio"]:checked')]
          .map((el) => (el instanceof HTMLInputElement ? el.value : ''))
          .filter(Boolean)
      : [];

    if (productTitle && selected.length) return `${productTitle} - ${selected.join(' / ')}`;
    return productTitle || selected.join(' / ') || '';
  }

  /**
   * @param {Event} event
   */
  #onWindowSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (this.#formId(form) !== this.dataset.formId) return;
    this.#onSubmit(event);
  }

  /**
   * @param {Event} event
   */
  #onSubmit(event) {
    const select = this.#getSelect();
    const option = select?.selectedOptions[0];
    const label = option?.value?.trim() || this.dataset.selectedLabel || '';
    const addonVariantId = option?.dataset.variantId || this.dataset.addonVariantId || '';

    if (!label || !addonVariantId) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const form = this.#getForm() || (event.target instanceof HTMLFormElement ? event.target : null);
    if (!form) return;

    const formData = new FormData(form);
    const parentVariantId = formData.get('id');
    if (!parentVariantId) return;

    const quantity = Number(formData.get('quantity')) || 1;
    const propertyKey = this.dataset.propertyKey || 'Diamants supplémentaires (facultatif)';
    // Fresh unique link every ATC → separate line items, no qty merge.
    const itemKey = this.#createItemKey();
    const lineUid = this.#createItemKey();
    this.dataset.itemKey = itemKey;
    this.dataset.selectedLabel = label;
    this.dataset.addonVariantId = addonVariantId;

    /** @type {Record<string, string>} */
    const parentProperties = {};
    for (const [key, value] of formData.entries()) {
      const match = String(key).match(/^properties\[(.+)\]$/);
      if (!match) continue;
      const propKey = match[1];
      if (!propKey || propKey.startsWith('_')) continue;
      if (propKey === propertyKey) continue;
      if (value !== '' && value != null) parentProperties[propKey] = String(value);
    }
    parentProperties[propertyKey] = label;
    parentProperties._itemKey = itemKey;
    parentProperties._addOnVariant = `${addonVariantId}:1:0`;
    parentProperties._lineUid = lineUid;

    const parentTitle = this.#getParentTitle(form);

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    /** @type {Record<string, string>} */
    const addonProperties = {
      _parentKey: itemKey,
      _lineUid: this.#createItemKey(),
    };
    if (parentTitle) addonProperties._parentTitle = parentTitle;

    const body = {
      items: [
        {
          id: Number(parentVariantId),
          quantity,
          properties: parentProperties,
        },
        {
          id: Number(addonVariantId),
          quantity: 1,
          properties: addonProperties,
        },
      ],
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };

    const productForm = form.closest('product-form-component');

    fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify(body) }))
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          productForm?.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );
          return;
        }

        const eventTarget = productForm || document;
        eventTarget.dispatchEvent(
          new CartAddEvent({}, String(parentVariantId), {
            source: 'product-form-component',
            itemCount: quantity + 1,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
      });
  }
}

if (!customElements.get('additional-diamonds-component')) {
  customElements.define('additional-diamonds-component', AdditionalDiamondsComponent);
}
